'use client';
import { CopyButton } from '@sbercloud/uikit-product-copy-line';
import { Highlight, Prism, themes } from 'prism-react-renderer';
import bashLang from 'refractor/lang/bash';
import httpsLang from 'refractor/lang/http';

import s from './styles.module.scss';
bashLang(Prism);
httpsLang(Prism);

import { CSS_BREAKPOINTS } from '@sbercloud/uikit-product-utils';
import cn from 'classnames';
import { useState } from 'react';

import { DATA_TEST_IDS_INJECTORS } from '@/constant/tests';
import { useServerDataCtx } from '@/hooks/ctx';
import { useLayoutType } from '@/hooks/useLayoutType';
import { isLoverThan } from '@/utils/layout';

import { Condition } from '../Condition';

type BaseLanguage =
  | 'markup'
  | 'jsx'
  | 'tsx'
  | 'swift'
  | 'kotlin'
  | 'objectivec'
  | 'js-extras'
  | 'reason'
  | 'rust'
  | 'graphql'
  | 'yaml'
  | 'go'
  | 'cpp'
  | 'markdown'
  | 'python'
  | 'json';

type ExternalLanguage = 'bash' | 'http';

type HighLightCodeProps = {
  lang: BaseLanguage | ExternalLanguage;
  code: string;
  withLineNumbers?: boolean;
};

export function HighLightCode({ lang, code, withLineNumbers }: HighLightCodeProps) {
  const [isVisibleCopyButton, setIsVisibleCopyButton] = useState(false);
  const { headers } = useServerDataCtx();
  const layoutType = useLayoutType(CSS_BREAKPOINTS, headers.xLayoutType);

  return (
    <div
      data-test-id={DATA_TEST_IDS_INJECTORS.highlight}
      onMouseLeave={() => setIsVisibleCopyButton(false)}
      onMouseEnter={() => setIsVisibleCopyButton(true)}
      className={s.high_light_wrapper}
    >
      <CopyButton
        className={cn(s.copy_button, {
          [s.display]: isVisibleCopyButton || isLoverThan('tablet', layoutType),
        })}
        valueToCopy={code}
      />

      <Highlight prism={Prism} theme={themes.github} code={code} language={lang}>
        {({ className, style, tokens, getLineProps, getTokenProps }) => (
          <pre className={cn(className, s.high_light_content_wrapper)} style={{ ...style }}>
            {tokens.map((line, i) => (
              <div key={i} {...getLineProps({ line })}>
                <Condition when={Boolean(withLineNumbers)}>
                  <span className={s.line_number}>{i + 1}</span>
                </Condition>
                {line.map((token, key) => (
                  <span key={key} {...getTokenProps({ token })} />
                ))}
              </div>
            ))}
          </pre>
        )}
      </Highlight>
    </div>
  );
}
